import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { scheduledReportsApi, groupsApi, devicesApi } from '../services/api';
import { useTenantContextStore } from '../store/tenant-context.store';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  Calendar,
  Clock,
  Mail,
  FileText,
  FileSpreadsheet,
  Play,
  Pause,
  Trash2,
  Plus,
  Edit,
  Loader2,
  CheckCircle,
  XCircle,
  Send,
  AlertTriangle,
  X,
  Truck,
} from 'lucide-react';
import clsx from 'clsx';

interface ScheduledReport {
  id: string;
  name: string;
  description?: string;
  isActive: boolean;
  reportType: 'SUMMARY' | 'TRIPS' | 'STOPS' | 'EVENTS';
  format: 'PDF' | 'EXCEL';
  frequency: 'DAILY' | 'WEEKLY' | 'MONTHLY';
  dayOfWeek?: number;
  dayOfMonth?: number;
  hour: number;
  minute: number;
  timezone: string;
  deviceIds: string[];
  groupId?: string;
  recipients: string[];
  lastRunAt?: string;
  lastRunStatus?: string;
  lastRunError?: string;
  nextRunAt?: string;
  createdAt: string;
}

const reportTypeLabels: Record<string, string> = {
  SUMMARY: 'Résumé',
  TRIPS: 'Trajets',
  STOPS: 'Arrêts',
  EVENTS: 'Événements',
};

type ReportType = 'SUMMARY' | 'TRIPS' | 'STOPS' | 'EVENTS';
type ReportFormat = 'PDF' | 'EXCEL';
type Frequency = 'DAILY' | 'WEEKLY' | 'MONTHLY';

const dayOfWeekLabels: Record<number, string> = {
  1: 'Lundi',
  2: 'Mardi',
  3: 'Mercredi',
  4: 'Jeudi',
  5: 'Vendredi',
  6: 'Samedi',
  7: 'Dimanche',
};

function formatSchedule(report: ScheduledReport): string {
  const time = `${report.hour.toString().padStart(2, '0')}:${report.minute.toString().padStart(2, '0')}`;

  switch (report.frequency) {
    case 'DAILY':
      return `Tous les jours à ${time}`;
    case 'WEEKLY':
      return `Chaque ${dayOfWeekLabels[report.dayOfWeek || 1]} à ${time}`;
    case 'MONTHLY':
      return `Le ${report.dayOfMonth || 1} de chaque mois à ${time}`;
    default:
      return time;
  }
}

export default function ScheduledReportsPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { selectedTenant } = useTenantContextStore();
  const tenantKey = selectedTenant?.id || 'default';
  const [showModal, setShowModal] = useState(false);
  const [editingReport, setEditingReport] = useState<ScheduledReport | null>(null);
  const [formData, setFormData] = useState<{
    name: string;
    description: string;
    reportType: ReportType;
    format: ReportFormat;
    frequency: Frequency;
    dayOfWeek: number;
    dayOfMonth: number;
    hour: number;
    minute: number;
    deviceIds: string[];
    groupId: string;
    recipients: string;
  }>({
    name: '',
    description: '',
    reportType: 'SUMMARY',
    format: 'PDF',
    frequency: 'WEEKLY',
    dayOfWeek: 1,
    dayOfMonth: 1,
    hour: 8,
    minute: 0,
    deviceIds: [],
    groupId: '',
    recipients: '',
  });

  // Fetch scheduled reports
  const { data: reportsData, isLoading } = useQuery({
    queryKey: ['scheduled-reports', tenantKey],
    queryFn: () => scheduledReportsApi.list(),
  });
  const reports = (reportsData?.data?.data || []) as ScheduledReport[];

  // Fetch groups for form
  const { data: groupsData } = useQuery({
    queryKey: ['groups', tenantKey],
    queryFn: () => groupsApi.list(),
  });
  const groups = groupsData?.data?.data || [];

  // Fetch devices for form
  const { data: devicesData } = useQuery({
    queryKey: ['devices-all', tenantKey],
    queryFn: () => devicesApi.list({ limit: 500 }),
  });
  const devices = (devicesData?.data?.data || []) as Array<{ id: string; name: string; plate?: string }>;

  // Fetch email status
  const { data: emailStatusData } = useQuery({
    queryKey: ['email-status', tenantKey],
    queryFn: () => scheduledReportsApi.getEmailStatus(),
  });
  const emailConfigured = emailStatusData?.data?.data?.configured || false;

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: Parameters<typeof scheduledReportsApi.create>[0]) =>
      scheduledReportsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduled-reports'] });
      setShowModal(false);
      resetForm();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof scheduledReportsApi.update>[1] }) =>
      scheduledReportsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduled-reports'] });
      setShowModal(false);
      resetForm();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => scheduledReportsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduled-reports'] });
    },
  });

  const runMutation = useMutation({
    mutationFn: (id: string) => scheduledReportsApi.run(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduled-reports'] });
    },
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      scheduledReportsApi.update(id, { isActive }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduled-reports'] });
    },
  });

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      reportType: 'SUMMARY',
      format: 'PDF',
      frequency: 'WEEKLY',
      dayOfWeek: 1,
      dayOfMonth: 1,
      hour: 8,
      minute: 0,
      deviceIds: [],
      groupId: '',
      recipients: '',
    });
    setEditingReport(null);
  };

  const openCreateModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (report: ScheduledReport) => {
    setEditingReport(report);
    setFormData({
      name: report.name,
      description: report.description || '',
      reportType: report.reportType,
      format: report.format,
      frequency: report.frequency,
      dayOfWeek: report.dayOfWeek || 1,
      dayOfMonth: report.dayOfMonth || 1,
      hour: report.hour,
      minute: report.minute,
      deviceIds: report.deviceIds,
      groupId: report.groupId || '',
      recipients: report.recipients.join(', '),
    });
    setShowModal(true);
  };

  const handleSubmit = () => {
    const recipientsList = formData.recipients
      .split(',')
      .map((e) => e.trim())
      .filter((e) => e.length > 0);

    if (recipientsList.length === 0) {
      alert('Veuillez ajouter au moins un destinataire');
      return;
    }

    const data = {
      name: formData.name,
      description: formData.description || undefined,
      reportType: formData.reportType,
      format: formData.format,
      frequency: formData.frequency,
      dayOfWeek: formData.frequency === 'WEEKLY' ? formData.dayOfWeek : undefined,
      dayOfMonth: formData.frequency === 'MONTHLY' ? formData.dayOfMonth : undefined,
      hour: formData.hour,
      minute: formData.minute,
      deviceIds: formData.deviceIds.length > 0 ? formData.deviceIds : undefined,
      groupId: formData.groupId || undefined,
      recipients: recipientsList,
    };

    if (editingReport) {
      updateMutation.mutate({ id: editingReport.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('scheduled_reports.title')}</h1>
          <p className="text-gray-500 mt-1">{t('scheduled_reports.subtitle')}</p>
        </div>
        <button
          onClick={openCreateModal}
          disabled={!emailConfigured}
          className="inline-flex items-center gap-2 btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Plus className="h-4 w-4" />
          {t('scheduled_reports.new_report')}
        </button>
      </div>

      {/* Email Status Warning */}
      {!emailConfigured && (
        <div className="glass-card p-4 border-amber-500/30 bg-amber-500/10">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-medium text-amber-400">{t('scheduled_reports.email_not_configured_title')}</h3>
              <p className="text-sm text-gray-500 mt-1">
                {t('scheduled_reports.email_not_configured_body')}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Reports List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
        </div>
      ) : reports.length === 0 ? (
        <div className="glass-card p-16 text-center">
          <Calendar className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">{t('scheduled_reports.no_report')}</h3>
          <p className="text-gray-500">
            {t('scheduled_reports.no_report_hint')}
          </p>
        </div>
      ) : (
        <div className="grid gap-4">
          {reports.map((report) => (
            <div
              key={report.id}
              className={clsx(
                'glass-card p-4 transition-all',
                !report.isActive && 'opacity-60'
              )}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-medium text-gray-900">{report.name}</h3>
                    <span
                      className={clsx(
                        'px-2 py-0.5 text-xs font-medium rounded-full',
                        report.isActive
                          ? 'bg-green-500/20 text-green-400'
                          : 'bg-gray-200 text-gray-500'
                      )}
                    >
                      {report.isActive ? t('scheduled_reports.active') : t('scheduled_reports.inactive')}
                    </span>
                  </div>

                  {report.description && (
                    <p className="text-sm text-gray-500 mb-3">{report.description}</p>
                  )}

                  <div className="flex flex-wrap gap-4 text-sm">
                    <div className="flex items-center gap-2 text-gray-600">
                      {report.format === 'PDF' ? (
                        <FileText className="h-4 w-4 text-red-400" />
                      ) : (
                        <FileSpreadsheet className="h-4 w-4 text-green-400" />
                      )}
                      {reportTypeLabels[report.reportType]} ({report.format})
                    </div>

                    <div className="flex items-center gap-2 text-gray-600">
                      <Clock className="h-4 w-4 text-gray-400" />
                      {formatSchedule(report)}
                    </div>

                    <div className="flex items-center gap-2 text-gray-600">
                      <Mail className="h-4 w-4 text-gray-400" />
                      {t('scheduled_reports.recipients_count', { count: report.recipients.length })}
                    </div>
                  </div>

                  {/* Last run status */}
                  {report.lastRunAt && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <div className="flex items-center gap-2 text-sm">
                        {report.lastRunStatus === 'success' ? (
                          <CheckCircle className="h-4 w-4 text-green-400" />
                        ) : (
                          <XCircle className="h-4 w-4 text-red-400" />
                        )}
                        <span className="text-gray-500">
                          {t('scheduled_reports.last_run', { date: format(new Date(report.lastRunAt), "dd/MM/yyyy HH:mm", { locale: fr }) })}
                        </span>
                        {report.lastRunStatus === 'failed' && report.lastRunError && (
                          <span className="text-red-400 text-xs">- {report.lastRunError}</span>
                        )}
                      </div>
                      {report.nextRunAt && (
                        <div className="text-xs text-gray-400 mt-1">
                          {t('scheduled_reports.next_run', { date: format(new Date(report.nextRunAt), "dd/MM/yyyy HH:mm", { locale: fr }) })}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => runMutation.mutate(report.id)}
                    disabled={runMutation.isPending || !emailConfigured}
                    className="p-2 text-gray-500 hover:text-primary-500 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                    title={t('scheduled_reports.run_now')}
                  >
                    {runMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                  </button>
                  <button
                    onClick={() => toggleMutation.mutate({ id: report.id, isActive: !report.isActive })}
                    className="p-2 text-gray-500 hover:text-amber-400 hover:bg-gray-100 rounded-lg transition-colors"
                    title={report.isActive ? t('scheduled_reports.deactivate') : t('scheduled_reports.activate')}
                  >
                    {report.isActive ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                  </button>
                  <button
                    onClick={() => openEditModal(report)}
                    className="p-2 text-gray-500 hover:text-primary-500 hover:bg-gray-100 rounded-lg transition-colors"
                    title={t('scheduled_reports.edit')}
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => {
                      if (confirm(t('scheduled_reports.delete_confirm'))) {
                        deleteMutation.mutate(report.id);
                      }
                    }}
                    className="p-2 text-gray-500 hover:text-red-400 hover:bg-gray-100 rounded-lg transition-colors"
                    title={t('scheduled_reports.delete')}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowModal(false)} />
          <div className="relative bg-white rounded-2xl border border-gray-200 w-full max-w-xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingReport ? 'Modifier le rapport' : 'Nouveau rapport planifié'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Nom du rapport</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder={t('scheduled_reports.name_placeholder')}
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Description (optionnel)</label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder={t('scheduled_reports.description_placeholder')}
                />
              </div>

              {/* Report Type & Format */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Type de rapport</label>
                  <select
                    value={formData.reportType}
                    onChange={(e) => setFormData({ ...formData, reportType: e.target.value as typeof formData.reportType })}
                    className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-900 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="SUMMARY">Résumé</option>
                    <option value="TRIPS">Trajets</option>
                    <option value="STOPS">Arrêts</option>
                    <option value="EVENTS">Événements</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Format</label>
                  <select
                    value={formData.format}
                    onChange={(e) => setFormData({ ...formData, format: e.target.value as typeof formData.format })}
                    className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-900 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="PDF">PDF</option>
                    <option value="EXCEL">Excel</option>
                  </select>
                </div>
              </div>

              {/* Frequency */}
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Fréquence</label>
                <select
                  value={formData.frequency}
                  onChange={(e) => setFormData({ ...formData, frequency: e.target.value as typeof formData.frequency })}
                  className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-900 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="DAILY">Quotidien</option>
                  <option value="WEEKLY">Hebdomadaire</option>
                  <option value="MONTHLY">Mensuel</option>
                </select>
              </div>

              {/* Day selection based on frequency */}
              {formData.frequency === 'WEEKLY' && (
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Jour de la semaine</label>
                  <select
                    value={formData.dayOfWeek}
                    onChange={(e) => setFormData({ ...formData, dayOfWeek: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-900 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    {Object.entries(dayOfWeekLabels).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>
              )}

              {formData.frequency === 'MONTHLY' && (
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Jour du mois</label>
                  <select
                    value={formData.dayOfMonth}
                    onChange={(e) => setFormData({ ...formData, dayOfMonth: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-900 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    {Array.from({ length: 28 }, (_, i) => i + 1).map((day) => (
                      <option key={day} value={day}>{day}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Time */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Heure</label>
                  <select
                    value={formData.hour}
                    onChange={(e) => setFormData({ ...formData, hour: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-900 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    {Array.from({ length: 24 }, (_, i) => i).map((h) => (
                      <option key={h} value={h}>{h.toString().padStart(2, '0')}h</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Minute</label>
                  <select
                    value={formData.minute}
                    onChange={(e) => setFormData({ ...formData, minute: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-900 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    {[0, 15, 30, 45].map((m) => (
                      <option key={m} value={m}>{m.toString().padStart(2, '0')}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Device/Group filter */}
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Groupe (optionnel)</label>
                <select
                  value={formData.groupId}
                  onChange={(e) => setFormData({ ...formData, groupId: e.target.value, deviceIds: [] })}
                  className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-900 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Aucun groupe</option>
                  {groups.map((group: { id: string; name: string }) => (
                    <option key={group.id} value={group.id}>{group.name}</option>
                  ))}
                </select>
              </div>

              {/* Individual device selection (only when no group selected) */}
              {!formData.groupId && (
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">
                    Véhicules ({formData.deviceIds.length === 0 ? 'tous' : `${formData.deviceIds.length} sélectionné(s)`})
                  </label>
                  <div className="max-h-40 overflow-y-auto border border-gray-300 rounded-lg bg-gray-100">
                    {devices.length === 0 ? (
                      <div className="px-3 py-2 text-sm text-gray-400">Aucun véhicule disponible</div>
                    ) : (
                      devices.map((device) => (
                        <label
                          key={device.id}
                          className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-200 cursor-pointer transition-colors"
                        >
                          <input
                            type="checkbox"
                            checked={formData.deviceIds.includes(device.id)}
                            onChange={(e) => {
                              const ids = e.target.checked
                                ? [...formData.deviceIds, device.id]
                                : formData.deviceIds.filter((id) => id !== device.id);
                              setFormData({ ...formData, deviceIds: ids });
                            }}
                            className="rounded border-gray-300 text-primary-500 focus:ring-primary-500/20"
                          />
                          <Truck className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                          <span className="text-sm text-gray-900 truncate">
                            {device.name}{device.plate ? ` (${device.plate})` : ''}
                          </span>
                        </label>
                      ))
                    )}
                  </div>
                  {formData.deviceIds.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, deviceIds: [] })}
                      className="mt-1 text-xs text-primary-500 hover:text-primary-600"
                    >
                      Tout désélectionner (= tous les véhicules)
                    </button>
                  )}
                </div>
              )}

              {/* Recipients */}
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Destinataires (emails séparés par des virgules)
                </label>
                <textarea
                  value={formData.recipients}
                  onChange={(e) => setFormData({ ...formData, recipients: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="email1@example.com, email2@example.com"
                />
              </div>
            </div>

            <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4 flex justify-end gap-3">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleSubmit}
                disabled={!formData.name || !formData.recipients || createMutation.isPending || updateMutation.isPending}
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {createMutation.isPending || updateMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : editingReport ? (
                  'Mettre à jour'
                ) : (
                  'Créer'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
